export function getApiBase(): string {
  // Use a relative URL so the request hits whatever origin the dashboard was
  // served from. The Replit proxy routes `/api` to the API artifact (see
  // artifacts/api-server/.replit-artifact/artifact.toml) in both dev and prod,
  // and a same-origin request guarantees the session cookie is attached even
  // when the dashboard is embedded inside the canvas iframe.
  return "/api";
}

/**
 * Structured error thrown by apiFetch on non-2xx. Carries the HTTP status and
 * any circuit-breaker hint from the server so callers can render a friendly
 * "service is cooling down" banner instead of a raw error string.
 */
export class ApiError extends Error {
  status: number;
  circuitOpen: boolean;
  retryAfterMs?: number;
  contentFilter?: boolean;
  /** True when the API said the user isn't signed in. The auth context
   *  watches for this so it can drop into the LoginScreen automatically. */
  notAuthenticated: boolean;
  /** True when the user is signed in but lacks permission for this resource. */
  forbidden: boolean;
  /** Raw parsed body (best-effort) for callers that need extra fields. */
  body: Record<string, unknown>;
  constructor(message: string, status: number, body: Record<string, unknown>) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
    this.circuitOpen = body["circuitOpen"] === true || body["error"] === "circuit_open";
    this.retryAfterMs = typeof body["retryAfterMs"] === "number" ? (body["retryAfterMs"] as number) : undefined;
    this.contentFilter = body["contentFilter"] === true;
    this.notAuthenticated = status === 401 || body["error"] === "not_authenticated";
    this.forbidden = status === 403 || body["error"] === "forbidden";
  }
}

/**
 * Pub-sub for global 401s — the AuthContext subscribes so that when ANY
 * apiFetch surfaces a 401 we drop straight to the login screen, not just
 * the call site that happened to notice. Keeps the redirect coherent.
 */
type Listener = () => void;
const unauthorizedListeners = new Set<Listener>();
export function onUnauthorized(fn: Listener): () => void {
  unauthorizedListeners.add(fn);
  return () => unauthorizedListeners.delete(fn);
}

export async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  // `credentials: 'include'` is required because the dashboard (port 5173/etc)
  // and the API (port 8080) are different origins, and our session cookie
  // must travel across that boundary.
  const resp = await fetch(`${getApiBase()}${path}`, {
    ...options,
    credentials: "include",
  });
  if (!resp.ok) {
    const body = (await resp.json().catch(() => ({}))) as Record<string, unknown>;
    const msg = (body["message"] as string | undefined)
      ?? (typeof body["error"] === "string" ? (body["error"] as string) : undefined)
      ?? `API ${path}: ${resp.status}`;
    const err = new ApiError(msg, resp.status, body);
    if (err.notAuthenticated) {
      // Notify listeners (AuthContext) so the app re-renders into LoginScreen.
      // We still throw so the caller's try/catch sees the failure.
      for (const fn of unauthorizedListeners) {
        try { fn(); } catch { /* swallow listener errors */ }
      }
    }
    throw err;
  }
  return resp.json() as Promise<T>;
}
